import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { Util } from "@core/Util";
import { Patch } from "@shared/models/common";
import {
  ICourseAddModel,
  ICourseEditModel,
  IStudyPlanAddVersionModel,
  IStudyPlanCopyVersionModel,
  IStudyPlanCourse,
  IStudyPlanCreateModel,
  IStudyPlanPeriodInfo,
  IStudyPlanVersion,
  IPeriodEditModel,
  IStudyPlan,
  IStudyPlanListItem,
  IStudyPlanVersionSearchResult,
  IStudyPlanVersionCompare,
  IStudyPlanPendingApproval,
  IStudyPlanFinishedApproval,
  IStudyPlanCopyVersionResult,
  IStudyPlanUpdateVersionModel,
  IStudyPlanAlertItem,
  ICoursePlanning,
  IStudyPlanFinishResult,
  StudyPlanCourseAlertLevel,
  IStudyPlanPeriod,
  IStudyPlanSkillAddModel,
  IStudyPlanSkillEditModel,
  IStudyPlanSkill,
  IPopularSkillImplementer,
  IStudyPlanSkillCopyTarget
} from "@shared/models/StudyPlan";

import { environment } from "src/environments/environment";
import { IStudyPlanSkillReport } from "../models/StudyPlanSkillReport";

interface IFindVersionCriteria {
  id?: number[];
  implementedProgrammeId?: number[];
  isStandard?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class StudyPlanService {
  constructor(private http: HttpClient) { }

  get apiUrl(): string {
    return `${environment.apiUrl}/studyPlans`;
  }

  /**
   * Get study plans.
   * @param implementedProgrammeId Implemented programme id
   * @param criteria Optional: Criterias for additional filtration
   * @returns Http request observable returns study plan list item array
   */
  get(options: {
    implementedProgrammeId: number,
    periodId?: number,
    flowId?: string,
    isStandard?: boolean
  }): Observable<IStudyPlanListItem[]> {
    const params = Object.keys(options).reduce((all, t) => {
      all[t] = options[t];
      return all;
    }, {});

    return this.http.get<IStudyPlanListItem[]>(this.apiUrl, { params });
  }

  /**
   * Get study plan by id
   * @param id Study plan id
   * @returns Http request observable returns study plan
   */
  getById(id: number): Observable<IStudyPlan> {
    return this.http.get<IStudyPlan>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get study plan versions by study plan id
   * @param id Study plan id
   * @returns Http request observable returns study plan version array
   */
  getVersionsById(id: number): Observable<IStudyPlanVersion[]> {
    return this.http.get<IStudyPlanVersion[]>(`${this.apiUrl}/${id}/versions`);
  }

  ///**
  // * Get study plan versions by study plan id
  // * @param id Study plan id
  // * @returns Http request observable returns study plan version array
  // */
  //getVersionsByimplementedProgramme(implementedProgrammeId: number): Observable<IStudyPlanVersion[]> {
  //  return this.http.get<IStudyPlanVersion[]>(`${this.apiUrl}/versions?implementedProgrammeId=${implementedProgrammeId}`);
  //}

  /**
   * Create new study plan
   * @param item Study plan create model
   * @returns Http request observable returns study plan id
   */
  create(item: IStudyPlanCreateModel): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}`, item);
  }

  /**
   * Delete study plan
   * @param id Study plan id
   * @returns Http request observable
   */
  delete(id: number): Observable<any> {
    const url = `${this.apiUrl}/${id}`;
    return this.http.delete(url);
  }

  /**
   * Export study plan.
   * @param id Study plan id
   * @param periodIds Study plan period IDs to include
   * @param isEnglish Should export using document template english file
   */
  export(id: number, periodIds: number[], isEnglish: boolean = false): Observable<any> {
    const url = `${this.apiUrl}/export/${id}?isEnglish=${isEnglish}`;
    return Util.downloadFile(url, this.http, periodIds);
  }

  /**
   * Create study plan version
   * @param id Study plan id
   * @param item Study plan add version model
   * @returns Http request observable returns study plan version id
   */
  createVersion(
    id: number,
    item: IStudyPlanAddVersionModel
  ): Observable<number> {
    return this.http.post<any>(`${this.apiUrl}/${id}/versions`, item);
  }

  /**
   * Update study plan version
   * @param versionId Study plan version id
   * @param item Study plan update version model
   * @returns Http request observable
   */
  updateVersion(
    versionId: number,
    item: IStudyPlanUpdateVersionModel
  ): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/versions/${versionId}`, item);
  }

  /**
   * Set study plan version accreditation state
   * @param versionId Study plan version id
   * @param isAccredited Is accredited
   * @returns Http request observable
   */
  setAccreditation(versionId: number, isAccredited: boolean): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}/versions/${versionId}/accreditation`,
      isAccredited
    );
  }

  /**
   * Copy study plan version
   * @param versionId Study plan version id
   * @param item Study plan copy version model
   * @returns Http request observable returns study plan copy version result
   */
  copyVersion(
    versionId: number,
    item: IStudyPlanCopyVersionModel
  ): Observable<IStudyPlanCopyVersionResult> {
    return this.http.post<any>(
      `${this.apiUrl}/versions/${versionId}/copy`,
      item
    );
  }

  /**
   * Delete study plan version
   * @param versionId Study plan version id
   * @returns Http request observable
   */
  deleteVersion(versionId: number): Observable<any> {
    const url = `${this.apiUrl}/versions/${versionId}`;
    return this.http.delete(url);
  }

  /**
   * Add study course to study plan version
   * @param versionId Study plan version id
   * @param item Course add model
   * @returns Http request observable
   */
  addCourse(versionId: number, item: ICourseAddModel): Observable<number[]> {
    return this.http.post<number[]>(`${this.apiUrl}/versions/${versionId}/courses`, {
      ...item,
      version: Util.getDateString(item.version, 'y-m-d')
    });
  }

  /**
   * Update study plan course
   * @param studyPlanCourseId Study plan course id
   * @param item Course edit model
   * @returns Http request observable
   */
  updateCourse(
    studyPlanCourseId: number,
    item: ICourseEditModel
  ): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}/courses/${studyPlanCourseId}`,
      item
    );
  }

  /**
   * Patch study plan course
   * @param studyPlanCourseId Study plan course id
   * @param data Course edit model patch
   * @returns Http request observable
   */
  patchCourse(
    studyPlanCourseId: number,
    data: Patch<ICourseEditModel>
  ): Observable<any> {
    return this.http.patch(
      `${this.apiUrl}/courses/${studyPlanCourseId}`,
      Util.createPatchOperations(data)
    );
  }

  /**
   * Remove study plan course
   * @param studyPlanCourseId Study plan course id
   * @param allPeriods Optional: All periods
   * @param discontinue Optional: Discontinue
   * @returns Http request observable
   */
  removeCourse(
    studyPlanCourseId: number,
    allPeriods?: boolean,
    discontinue?: boolean
  ): Observable<any> {
    const url = `${this.apiUrl}/courses/${studyPlanCourseId}`;
    const params: any = {};

    if (allPeriods !== undefined) params.allPeriods = allPeriods;
    if (discontinue !== undefined) params.discontinue = discontinue;

    return this.http.delete(url, { params });
  }

  /**
   * Replace study plan course
   * @param studyPlanCourseId Study plan course id
   * @param item Course add model
   * @returns Http request observable returns replaced study plan course id array
   */
  replaceCourse(
    studyPlanCourseId: number,
    item: ICourseAddModel
  ): Observable<number[]> {
    return this.http.post<number[]>(
      `${this.apiUrl}/courses/${studyPlanCourseId}/replace`,
      item
    );
  }

  /**
   * Actualize study plan course
   * @param studyPlanCourseId Study plan course id
   * @returns Http request observable returns actualized version number
   */
  actualizeCourse(studyPlanCourseId: number): Observable<number> {
    return this.http.post<number>(
      `${this.apiUrl}/courses/${studyPlanCourseId}/actualize`,
      null
    );
  }

  /**
   * Refresh courses by version id
   * @param versionId Study plan version id
   * @returns Http request observable
   */
  refreshCourses(versionId: number): Observable<any> {
    return this.http.post<number>(
      `${this.apiUrl}/versions/${versionId}/courses/refresh`,
      null
    );
  }

  /**
   * Update study plan period
   * @param studyPlanPeriodId Study plan period id
   * @param item Period edit model
   * @returns Http request observable
   */
  updatePeriod(
    studyPlanPeriodId: number,
    item: IPeriodEditModel
  ): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}/periods/${studyPlanPeriodId}`,
      item
    );
  }

  /**
   * Patch study plan period
   * @param studyPlanPeriodId Study plan period id
   * @param data Period edit model patch
   * @returns Http request observable
   */
  patchPeriod(
    studyPlanPeriodId: number,
    data: Patch<IPeriodEditModel>
  ): Observable<any> {
    return this.http.patch(
      `${this.apiUrl}/periods/${studyPlanPeriodId}`,
      Util.createPatchOperations(data)
    );
  }

  /**
   * Get courses by version id
   * @param versionId Study plan version id
   * @returns Http request observable returns study plan course array
   */
  getCourses(versionId: number): Observable<IStudyPlanCourse[]> {
    return this.http.get<any>(`${this.apiUrl}/versions/${versionId}/courses`);
  }

  /**
   * Get periods by version id or (version id and study plan course code)
   * @param versionId Study plan version id
   * @param courseCode Optional: Course code
   * @returns Http request observable returns study plan semester array
   */
  getPeriodInfo(versionId: number, courseCode?: string): Observable<IStudyPlanPeriodInfo[]> {
    const params: any = {};
    const url = `${this.apiUrl}/versions/${versionId}/periodInfo`;

    if (courseCode) params.courseCode = courseCode;

    return this.http.get<IStudyPlanPeriodInfo[]>(url, { params });
  }

  /**
   * Get periods by version id or (version id and study plan course code)
   * @param versionId Study plan version id
   * @param courseCode Optional: Course code
   * @returns Http request observable returns study plan semester array
   */
  getPeriods(versionId: number): Observable<IStudyPlanPeriod[]> {
    const url = `${this.apiUrl}/versions/${versionId}/periods`;
    return this.http.get<IStudyPlanPeriod[]>(url);
  }

  /**
   * Get course planning
   * @param courseId Course id
   * @param studyProgrammeId Study programme id
   * @param implementedProgrammeId Implemented programme id
   * @returns Http request observable returns course planning array
   */
  getPlannings(
    courseId: number,
    studyProgrammeId?: number,
    implementedProgrammeId?: number
  ): Observable<ICoursePlanning[]> {
    const params: any = { courseId };

    if (studyProgrammeId) params.studyProgrammeId = studyProgrammeId;
    if (implementedProgrammeId)
      params.implementedProgrammeId = implementedProgrammeId;

    return this.http.get<ICoursePlanning[]>(`${this.apiUrl}/plannings`, {
      params,
    });
  }

  /**
   * Find study plan versions
   * @param criteria Find version criteria
   * @returns Http request observable returns study plan version search result array
   */
  findVersions(
    criteria: IFindVersionCriteria
  ): Observable<IStudyPlanVersionSearchResult[]> {
    const params: any = {};

    if (criteria) {
      Object.keys(criteria).forEach((k) => {
        if (criteria[k]) params[k] = criteria[k];
      });
    }

    return this.http.get<IStudyPlanVersionSearchResult[]>(
      `${this.apiUrl}/versions/find`,
      { params }
    );
  }

  /**
   * Compare study plan versions
   * @param versionIds Study plan version id array
   * @returns Http request observable returns study plan version compare array
   */
  compareVersions(
    versionIds: number[]
  ): Observable<IStudyPlanVersionCompare[]> {
    const params: any = { versionId: versionIds };
    return this.http.get<IStudyPlanVersionCompare[]>(
      `${this.apiUrl}/versions/compare`,
      { params }
    );
  }

  /**
   * Get study plans containing alerts.
   * @param level List of alert levels to include
   * @returns Http request observable returns study plan alert item array
   */
  getAlerts(level: StudyPlanCourseAlertLevel[]): Observable<IStudyPlanAlertItem[]> {
    const url = `${this.apiUrl}/alerts`;
    return this.http.get<IStudyPlanAlertItem[]>(url, {
      params: {
        level: level.join(',')
      }
    });
  }

  /**
   * Finish a study plan version.
   * @param versionId
   * @returns
   */
  finish(versionId: number): Observable<any> {
    const url = `${this.apiUrl}/versions/${versionId}/finish`;
    return this.http.post(url, null);
  }

  /**
   * Finish old study plans.
   * @returns Number of updated plan versions and the list of programmes containing unifinished plans.
   */
  finishOld(): Observable<IStudyPlanFinishResult> {
    const url = `${this.apiUrl}/finish`;
    return this.http.post<IStudyPlanFinishResult>(url, null);
  }

  /**
   * Start study plan approval
   * @param id Study plan id
   * @returns Http request observable
   */
  startApproval(id: number): Observable<any> {
    const url = `${this.apiUrl}/${id}/approval/start`;
    return this.http.post(url, null);
  }

  /**
   * Cancel study plan approval
   * @param id Study plan id
   * @returns Http request observable
   */
  cancelApproval(id: number): Observable<any> {
    const url = `${this.apiUrl}/${id}/approval/cancel`;
    return this.http.post(url, null);
  }

  /**
   * Send study plans for approval
   * @param ids Study plan id array
   * @param message Message
   * @returns Http request observable
   */
  sendForApproval(ids: number[], message: string): Observable<any> {
    const url = `${this.apiUrl}/approval/send`;
    return this.http.post(url, { ids, message });
  }

  /**
   * Notify approval initiators
   * @param ids Study plan id array
   * @param message Message
   * @returns Http request observable
   */
  notifyApprovalInitiators(ids: number[], message: string): Observable<any> {
    const url = `${this.apiUrl}/approval/notify`;
    return this.http.post(url, { ids, message });
  }

  /**
   * Approve study plan
   * @param id Study plan id
   * @param comment Optional: Comment
   * @returns Http request observable
   */
  approve(id: number, comment?: string): Observable<any> {
    const url = `${this.apiUrl}/${id}/approval/approve`;
    return this.http.post(url, { comment });
  }

  /**
   * Reject study plan
   * @param id Study plan id
   * @param comment Comment
   * @param withChanges Optional: Reject with changes
   * @returns Http request observable
   */
  reject(id: number, comment: string, withChanges?: boolean): Observable<any> {
    const url = `${this.apiUrl}/${id}/approval/reject`;
    return this.http.post(url, { comment, withChanges });
  }

  /**
   * Accpet study plan changes
   * @param id Study plan id
   * @param comment Optional: Comment
   * @returns Http request observable
   */
  acceptChanges(id: number, comment?: string): Observable<any> {
    const url = `${this.apiUrl}/${id}/approval/acceptChanges`;
    return this.http.post(url, { comment });
  }

  /**
   * Get study plan array with started approval status
   * @param studyProgrammeId Study programme id
   * @returns Http request observable returns study plan started approval array
   */
  getStartedApprovals(
    studyProgrammeId: number
  ): Observable<IStudyPlanPendingApproval[]> {
    const url = `${this.apiUrl}/approval/started`;
    return this.http.get<IStudyPlanPendingApproval[]>(url, {
      params: { studyProgrammeId },
    });
  }

  /**
   * Get study plan array with pending approval status
   * @param studyProgrammeId Study programme id
   * @returns Http request observable returns study plan pending approval array
   */
  getPendingApprovals(
    studyProgrammeId: number
  ): Observable<IStudyPlanPendingApproval[]> {
    const url = `${this.apiUrl}/approval/pending`;
    return this.http.get<IStudyPlanPendingApproval[]>(url, {
      params: { studyProgrammeId },
    });
  }

  /**
   * Get study plan array with finished approval status
   * @param studyProgrammeId Study programme id
   * @returns Http request observable returns study plan finished approval array
   */
  getFinishedApprovals(
    studyProgrammeId: number
  ): Observable<IStudyPlanFinishedApproval[]> {
    const url = `${this.apiUrl}/approval/finished`;
    return this.http.get<IStudyPlanFinishedApproval[]>(url, {
      params: { studyProgrammeId },
    });
  }

  /**
   * Get study plan version skills.
   * @param versionId Study plan version ID
   * @returns
   */
  getSkills(versionId: number): Observable<IStudyPlanSkill[]> {
    return this.http.get<IStudyPlanSkill[]>(`${this.apiUrl}/versions/${versionId}/skills`);
  }

  /**
   * Add a skill to study plan version.
   * @param versionId Study plan version ID
   * @param item Skill data
   * @returns
   */
  addSkill(versionId: number, item: IStudyPlanSkillAddModel): Observable<number> {
    return this.http.post<number>(`${this.apiUrl}/versions/${versionId}/skills`, item);
  }

  /**
   * Update study plan skill.
   * @param studyPlanCourseId Study plan skill ID
   * @param item Skill data
   * @returns
   */
  updateSkill(studyPlanSkillId: number, item: IStudyPlanSkillEditModel): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/skills/${studyPlanSkillId}`, item);
  }

  /**
   * Patch study plan skill.
   * @param studyPlanCourseId Study plan skill ID
   * @param data Skill data patch
   * @returns
   */
  patchSkill(studyPlanSkillId: number, data: Patch<IStudyPlanSkillEditModel>): Observable<any> {
    return this.http.patch(`${this.apiUrl}/skills/${studyPlanSkillId}`, Util.createPatchOperations(data));
  }

  /**
   * Remove study plan course
   * @param studyPlanCourseId Study plan skill ID
   * @returns
   */
  removeSkill(studyPlanSkillId: number): Observable<any> {
    const url = `${this.apiUrl}/skills/${studyPlanSkillId}`;
    return this.http.delete(url);
  }

  /**
   * Export skills to the file.
   * @param versionId Study plan version ID
   * @param type Report type
   * @returns
   */
  exportSkills(versionId: number, type: 'Summary' | 'ByLevel'): Observable<any> {
    const url = `${this.apiUrl}/versions/${versionId}/skills/export?type=${type}`;
    return Util.downloadFile(url, this.http);
  }

  /**
   * Import skills based on the study plan courses.
   * @param versionId Study plan version ID
   * @param overwrite
   * @returns Appended study plan skill IDs
   */
  importSkills(versionId: number, overwrite: boolean = false): Observable<number[]> {
    let url = `${this.apiUrl}/versions/${versionId}/skills/import`;

    if (overwrite) url += '?overwrite=true';

    return this.http.post<number[]>(url, null);
  }

  /**
   * Get applicable study plan versions to copy skills to.
   * @param versionId Source study plan version ID
   * @returns
   */
  getSkillCopyTargets(versionId: number): Observable<IStudyPlanSkillCopyTarget[]> {
    return this.http.get<IStudyPlanSkillCopyTarget[]>(`${this.apiUrl}/versions/${versionId}/skills/copyTargets`);
  }

  /**
   * Copy skills.
   * @param sourceVersionId Source study plan version ID
   * @param targetVersionId Target study plan version ID
   * @returns
   */
  copySkills(sourceVersionId: number, targetVersionId: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/versions/${sourceVersionId}/skills/copy`, { targetVersionId });
  }

  /**
   * Get study plan skill report.
   * @param versionId Study plan version ID
   * @returns
   */
  getSkillReport(versionId: number): Observable<IStudyPlanSkillReport> {
    return this.http.get<IStudyPlanSkillReport>(`${this.apiUrl}/versions/${versionId}/skills/report`);
  }

  /**
   * Get most popular skill implementers.
   * @returns
   */
  getPopularSkillImplementers(): Observable<IPopularSkillImplementer[]> {
    return this.http.get<IPopularSkillImplementer[]>(`${this.apiUrl}/skills/popularImplementers`);
  }

  /**
   * Get study plan skill permissions.
   * @param versionId Study plan version ID
   * @returns
   */
  getSkillPermissions(versionId: number): Observable<{ canEdit: boolean }> {
    return this.http.get<{ canEdit: boolean }>(`${this.apiUrl}/versions/${versionId}/skills/permissions`);
  }

  /**
   * Get study plan skill report notes.
   * @param versionId Study plan version ID
   * @returns
   */
  getSkillReportNotes(versionId: number): Observable<{ notes: string, skillCode: string }[]> {
    const url = `${this.apiUrl}/versions/${versionId}/skills/reportNotes`;
    return this.http.get<{ notes: string, skillCode: string }[]>(url);
  }

  /**
   * Update skill report notes.
   * @param versionId Study plan version ID
   * @param text Text
   * @param skillCode Skill code the note is bound to
   * @returns
   */
  updateSkillReportNotes(versionId: number, text: string, skillCode?: string): Observable<any> {
    const url = `${this.apiUrl}/versions/${versionId}/skills/reportNotes`;
    return this.http.put(url, { text, skillCode });
  }
}
